import React, { useMemo, useState, useEffect } from "react";
import { CCard, CCardGroup } from "@coreui/react";
import TranslationForm from "../translationForm";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { TranslationService } from "@/api";
import { translation } from "@/constants/translation/translation.translation";
import { messages } from "@/constants/translation/common.translation";
import validationHelper from "@/helpers/validationHelper";

/**
 * @name EditTranslation
 *
 * @description
 * The EditTranslation component allows users to edit existing translations. It fetches the
 * translation details based on the `translation_id` from the URL and pre-fills the form.
 *
 * The component:
 * - Loads the list of available languages.
 * - Fetches existing translation data and updates the form fields.
 * - Allows users to modify translations and submit changes.
 * - Displays success messages on successful updates and redirects to the translations list.
 *
 * @returns {JSX.Element} The JSX for rendering the translation edit page.
 */
const EditTranslation = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id: translation_id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [translationData, setTranslationData] = useState(null);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    // Fetch languages and translation data on component mount
    const fetchData = async () => {
      try {
        // Fetch languages from API
        const languagesResponse = await TranslationService.getLanguages();
        
        // Handle the actual API response structure: { message: '...', data: { "0": {...}, "1": {...} } }
        const languagesData = languagesResponse.data || languagesResponse;
        let languagesList = [];
        
        if (Array.isArray(languagesData)) {
          languagesList = languagesData;
        } else if (languagesData && typeof languagesData === 'object') {
          // Handle object with numeric keys like { "0": {...}, "1": {...} }
          // This is the actual structure from your API
          languagesList = Object.values(languagesData).map(lang => ({
            id: lang.id,
            name: lang.name,
            code: lang.code,
            isDefault: lang.isDefault,
            displayName: lang.name // Use the actual language name
          }));
        }
        
        setLanguages(languagesList);

        // Fetch translation details
        if (translation_id) {
          const response = await TranslationService.get(translation_id);
          setTranslationData(response.data);
        }
      } catch (error) {
        toast.error(t(messages.toast.translation.dataFetchFailed));
        console.error("Fetch translation error:", error);
        // Fallback to mock languages if API fails

      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchData();
  }, [translation_id]);

  const handleUpdateTranslation = async (values) => {
    setIsLoading(true);
    try {
      // Format data according to API requirements
      const updateData = {
        id: translation_id,
        key: values.key,
        translations: values.translations.map(translation => ({
          id: translation.id || undefined, // Include id if it exists for existing translations
          language_id: translation.language_id,
          title: translation.title
        }))
      };
      
      const response = await TranslationService.update(updateData);
      toast.success(response.message || t(messages.toast.translation.updated));
      navigate("/masters/translation/list");
    } catch (error) {
      toast.error(t(messages.toast.translation.updateFailed));
      console.error("Update translation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = useMemo(() => {
    if (!translationData) {
      return {
        key: "",
        translations: languages.map((lang) => ({
          title: "",
          language: lang.name, // Use actual language name
          language_id: lang.id,
          displayName: lang.name
        })),
      };
    }

    // Handle different possible API response structures
    const data = translationData.data || translationData;
    
    
    return {
      key: data.group ? `${data.group}.${data.key}` : data.key,
      translations: (data.translations || []).map((v) => {
        
        return {
          id: v.id,
          title: v.value || v.title, // Handle different response formats
          language: typeof v.language === 'string' ? v.language : (v.language?.name || v.languages?.name || `Language`),
          language_id: v.language_id,
          displayName: typeof v.language === 'string' ? v.language : (v.language?.name || v.languages?.name || `Language`)
        };
      }),
    };
  }, [translationData, languages]);
  
  const validationSchema = useMemo(
    () => validationHelper.translationValidation(),
    []
  );

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(translation.edit.main_heading)}</h1>
        {!isLoading && (
          <TranslationForm
            t={t}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleUpdateTranslation}
            isLoading={isLoading}
            disabled={true}
          />
        )}
      </CCard>
    </CCardGroup>
  );
};

export default EditTranslation;
